// Client-side image downscale + re-encode, run BEFORE upload.
//
// Why: previously the raw camera original (a 5–15 MB phone photo) was streamed
// verbatim to the server, which then thumbnailed it. That made uploads slow for
// no benefit. Here we downscale to a sensible display size and re-encode to
// WebP in the browser, so the payload that leaves the device is a fraction of
// the original — much faster upload — while the backend still stores a
// high-quality 1600px WebP for crisp display.
//
// Zero-dependency (canvas + createImageBitmap). Fully defensive: any failure
// (unsupported format such as HEIC that the browser can't decode, a canvas
// error, or a result that isn't actually smaller) falls back to the original
// File so an upload is never blocked or degraded by this optimization.

export type CompressOptions = {
  /** Longest-edge cap in px. Matches the backend's 1600px target. */
  maxEdge?: number;
  /** WebP quality, 0–1. */
  quality?: number;
  /** Output mime. WebP gives the best size/quality trade-off. */
  mimeType?: "image/webp" | "image/jpeg";
};

export async function compressImageFile(
  file: File,
  opts: CompressOptions = {},
): Promise<File> {
  const { maxEdge = 1600, quality = 0.85, mimeType = "image/webp" } = opts;

  // Skip non-images and animated GIFs (canvas would flatten the animation).
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;

  try {
    // `imageOrientation: "from-image"` bakes EXIF rotation into the pixels so
    // portrait phone photos don't come out sideways (the canvas drops EXIF).
    const bitmap = await createImageBitmap(file, {
      // Cast: "from-image" is valid per spec but missing from this TS DOM lib's
      // ImageOrientation union.
      imageOrientation: "from-image" as ImageBitmapOptions["imageOrientation"],
    }).catch(() => createImageBitmap(file));

    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, mimeType, quality),
    );
    // Some browsers can't encode WebP → retry as JPEG once before giving up.
    const finalBlob =
      blob ??
      (await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", quality),
      ));
    if (!finalBlob) return file;

    // Only adopt the compressed result if it's genuinely smaller (a tiny,
    // already-optimized image can re-encode larger).
    if (finalBlob.size >= file.size) return file;

    const ext = finalBlob.type === "image/webp" ? "webp" : "jpg";
    const base = file.name.replace(/\.[^.]+$/, "") || "image";
    return new File([finalBlob], `${base}.${ext}`, {
      type: finalBlob.type,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}
